import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Gauge, FileSignature, Headphones, Loader2, Sparkles, Copy,
  CheckCircle2, XCircle, AlertTriangle, Globe, Zap, Target, ShieldCheck,
  ArrowRight, Award, ClipboardList,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  assistantsCatalog,
  assistantWebsiteAudit,
  assistantProposalWriter,
  assistantSalesCoach,
  assistantProposalStreamUrl,
} from "@/lib/api";

const ICONS = {
  gauge: Gauge,
  "file-signature": FileSignature,
  headphones: Headphones,
};

// ---------------------------------------------------------------------------
// Hub — /portal/assistants
// ---------------------------------------------------------------------------
export default function AiAssistantsHub() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    document.title = "AI Assistants Hub — WebsiteTech.pro";
    assistantsCatalog()
      .then((d) => setItems(d.items || []))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load assistants."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <Link to="/portal" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="assistants-back">
          <ArrowLeft className="w-4 h-4" /> Back to portal
        </Link>
        <div className="mt-4 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-indigo-700 font-bold border border-indigo-200 bg-indigo-50 rounded-full px-3 py-1">
              <Sparkles className="w-3.5 h-3.5" /> New · powered by our AI stack
            </div>
            <h1 className="mt-4 text-3xl md:text-4xl font-bold text-slate-900" data-testid="assistants-title">
              AI Assistants Hub
            </h1>
            <p className="mt-2 text-slate-600 max-w-2xl text-base">
              A growing library of purpose-built AI assistants — each one solves a
              real small-business job in under a minute. Every result is auditable
              and can be routed into your LCC as a lead.
            </p>
          </div>
        </div>

        {err && <div className="mt-6 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3" data-testid="assistants-error">{err}</div>}
        {loading && <div className="mt-8 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {items.map((a) => {
            const Icon = ICONS[a.icon] || Sparkles;
            return (
              <Link key={a.key} to={`/portal/assistants/${a.key}`}
                data-testid={`assistant-card-${a.key}`}
                className="group rounded-2xl bg-white border border-slate-200 hover:border-indigo-400 hover:shadow-lg p-6 transition">
                <div className="w-11 h-11 rounded-lg bg-indigo-50 border border-indigo-200 grid place-items-center">
                  <Icon className="w-5 h-5 text-indigo-700" />
                </div>
                <div className="mt-4 text-lg font-bold text-slate-900">{a.label}</div>
                <div className="mt-2 text-sm text-slate-600 leading-relaxed">{a.tagline}</div>
                <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-indigo-700 group-hover:gap-2 transition-all">
                  {a.cta} <ArrowRight className="w-3 h-3" />
                </div>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                  <span>Model · {a.provider}</span>
                  <span>{a.model}</span>
                </div>
              </Link>
            );
          })}
        </div>

        <section className="mt-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-800 text-white p-8">
          <div className="flex items-start gap-4 flex-wrap">
            <ShieldCheck className="w-8 h-8 flex-shrink-0" />
            <div>
              <div className="text-lg font-bold">Every result is auditable.</div>
              <p className="mt-2 text-indigo-100 text-sm max-w-2xl">
                Each run is stored in <code className="bg-indigo-900/50 px-1.5 py-0.5 rounded">ai_assistants_runs</code> with the full input, the LLM output, the
                model that produced it and the timestamp — so you can replay, review or
                dispute anything the AI said.
              </p>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Router shell — picks assistant by key
// ---------------------------------------------------------------------------
export function AiAssistantDetail() {
  const { key } = useParams();
  if (key === "website-audit") return <WebsiteAuditor />;
  if (key === "proposal-writer") return <ProposalWriter />;
  if (key === "sales-coach") return <SalesCoach />;
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 pt-24 pb-16 text-center">
        <div className="text-2xl font-bold text-slate-900">Assistant not found</div>
        <Link to="/portal/assistants" className="mt-4 inline-flex items-center gap-1 text-indigo-700 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to hub
        </Link>
      </div>
      <Footer />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Shared shell wrapper
// ---------------------------------------------------------------------------
function AssistantShell({ Icon, title, subtitle, testIdPrefix, children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 pt-24 pb-16">
        <Link to="/portal/assistants" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid={`${testIdPrefix}-back`}>
          <ArrowLeft className="w-4 h-4" /> Back to hub
        </Link>
        <div className="mt-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-indigo-50 border border-indigo-200 grid place-items-center">
            <Icon className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid={`${testIdPrefix}-title`}>{title}</h1>
            <p className="text-slate-600 text-sm">{subtitle}</p>
          </div>
        </div>
        <div className="mt-8">{children}</div>
      </div>
      <Footer />
    </div>
  );
}


// ---------------------------------------------------------------------------
// 1. Website Auditor
// ---------------------------------------------------------------------------
function WebsiteAuditor() {
  const [url, setUrl] = useState("https://example.com");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr(""); setResult(null); setLoading(true);
    try {
      const r = await assistantWebsiteAudit({ url, email: email || null, consent });
      setResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Audit failed. Please retry.");
    } finally { setLoading(false); }
  };

  const bandColor = (s) => s >= 70 ? "emerald" : s >= 40 ? "amber" : "rose";

  return (
    <AssistantShell
      Icon={Gauge}
      title="AI Website Auditor"
      subtitle="Paste any URL — get an actionable SEO, speed and conversion audit in ~20 seconds."
      testIdPrefix="audit"
    >
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Website URL</label>
        <div className="mt-2 flex gap-2 flex-wrap">
          <div className="flex-1 min-w-[260px] relative">
            <Globe className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={url} onChange={(e) => setUrl(e.target.value)}
              data-testid="audit-url-input"
              placeholder="https://your-site.com"
              className="w-full pl-9 pr-3 py-3 rounded border border-slate-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none" />
          </div>
          <button onClick={submit} disabled={loading || !url.trim()}
            data-testid="audit-submit"
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded font-bold transition disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Auditing…</> : <><Zap className="w-4 h-4" /> Audit my site</>}
          </button>
        </div>
        <details className="mt-4 text-sm">
          <summary className="text-slate-500 cursor-pointer">Optional — email me the report</summary>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="audit-email-input"
              placeholder="you@company.com"
              className="px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500" />
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="audit-consent" />
              I agree to be contacted about my audit.
            </label>
          </div>
        </details>
        {err && <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3" data-testid="audit-error">{err}</div>}
      </div>

      {result && (
        <div className="mt-6 space-y-5">
          <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="audit-overall">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Overall score</div>
                <div className="mt-1 text-5xl font-bold text-slate-900">{result.audit.overall_score ?? "-"}</div>
                <div className="mt-1 text-slate-600 max-w-2xl">{result.audit.verdict}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Estimated uplift</div>
                <div className="mt-1 text-sm text-indigo-700 font-bold max-w-xs">{result.audit.estimated_uplift || "—"}</div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(result.audit.bands || {}).map(([k, v]) => {
                const tone = bandColor(v);
                return (
                  <div key={k} data-testid={`audit-band-${k}`}
                    className={`rounded-lg border p-4 bg-${tone}-50 border-${tone}-200`}
                    style={{
                      background: tone === "emerald" ? "#ecfdf5" : tone === "amber" ? "#fffbeb" : "#fef2f2",
                      borderColor: tone === "emerald" ? "#a7f3d0" : tone === "amber" ? "#fde68a" : "#fecaca",
                    }}>
                    <div className="text-[11px] uppercase tracking-widest font-bold text-slate-600">{k}</div>
                    <div className="mt-1 text-3xl font-bold text-slate-900">{v}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {result.audit.top_wins?.length > 0 && (
            <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="audit-wins">
              <div className="flex items-center gap-2 text-lg font-bold text-slate-900">
                <Award className="w-5 h-5 text-amber-600" /> Top wins
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {result.audit.top_wins.map((w, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 p-4">
                    <div className="text-sm font-bold text-slate-900">{w.title}</div>
                    <div className="mt-1 text-sm text-slate-600">{w.why}</div>
                    <div className="mt-3 flex gap-2 text-[10px] uppercase tracking-widest font-bold">
                      <span className="rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">Effort · {w.effort}</span>
                      <span className="rounded-full bg-indigo-100 text-indigo-700 px-2 py-0.5">Impact · {w.impact}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.audit.fixes_by_band && (
            <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="audit-fixes">
              <div className="text-lg font-bold text-slate-900">Fixes by area</div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(result.audit.fixes_by_band).map(([band, fixes]) => (
                  <div key={band} className="rounded-lg border border-slate-200 p-4">
                    <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{band}</div>
                    <ul className="mt-3 space-y-2">
                      {(fixes || []).map((f, i) => (
                        <li key={i} className="text-sm">
                          <div className="font-semibold text-slate-900">{f.issue}</div>
                          <div className="text-slate-600">{f.fix}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          <details className="rounded-2xl bg-white border border-slate-200 p-6">
            <summary className="text-sm font-bold text-slate-700 cursor-pointer">Raw signals we captured</summary>
            <pre className="mt-3 text-[11px] text-slate-600 whitespace-pre-wrap overflow-x-auto">{JSON.stringify(result.signals, null, 2)}</pre>
          </details>
        </div>
      )}
    </AssistantShell>
  );
}


// ---------------------------------------------------------------------------
// 2. Proposal Writer
// ---------------------------------------------------------------------------
function ProposalWriter() {
  const [form, setForm] = useState({
    brief: "Full metallurgical failure analysis for a broken turbine blade. AS9100 aerospace supplier, needs report in 5 business days, budget ~$3,500.",
    business_name: "L.I.T. Labs",
    tone: "professional",
    length: "standard",
    industry: "metallurgical testing",
    currency: "USD",
    email: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [streamText, setStreamText] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr(""); setResult(null); setStreamText(""); setLoading(true);
    try {
      const res = await fetch(assistantProposalStreamUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, email: form.email || null }),
      });
      if (!res.ok || !res.body) throw new Error(`Stream failed: ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let doneData = null;
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        // Parse SSE: split on blank lines
        const chunks = buf.split(/\n\n/);
        buf = chunks.pop() || "";
        for (const chunk of chunks) {
          const lines = chunk.split("\n");
          let event = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) event = line.slice(7).trim();
            else if (line.startsWith("data: ")) data += line.slice(6);
          }
          if (!data) continue;
          try {
            const parsed = JSON.parse(data);
            if (event === "delta" && parsed.delta) {
              acc += parsed.delta;
              setStreamText(acc);
            } else if (event === "done") {
              doneData = parsed;
            } else if (event === "error") {
              throw new Error(parsed.error || "stream error");
            }
          } catch (e) { /* skip malformed */ }
        }
      }
      if (doneData) {
        setResult({
          proposal_markdown: acc,
          quote_total: doneData.quote_total,
          currency: doneData.currency,
          run_id: doneData.run_id,
          lead: doneData.lead,
        });
      }
    } catch (e) {
      setErr(e?.message || "Proposal generation failed. Please retry.");
    } finally { setLoading(false); }
  };

  const copy = async () => {
    if (!result?.proposal_markdown) return;
    await navigator.clipboard.writeText(result.proposal_markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <AssistantShell
      Icon={FileSignature}
      title="AI Proposal Writer"
      subtitle="Describe the job — get a client-ready proposal, SOW and quote in markdown."
      testIdPrefix="proposal"
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6 h-fit sticky top-24">
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Your business</label>
          <input value={form.business_name} onChange={(e) => upd("business_name", e.target.value)}
            data-testid="proposal-business-input"
            className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500" />

          <label className="mt-4 block text-xs uppercase tracking-widest font-bold text-slate-500">Industry</label>
          <input value={form.industry} onChange={(e) => upd("industry", e.target.value)}
            data-testid="proposal-industry-input"
            className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500" />

          <label className="mt-4 block text-xs uppercase tracking-widest font-bold text-slate-500">Client brief</label>
          <textarea value={form.brief} onChange={(e) => upd("brief", e.target.value)}
            data-testid="proposal-brief-input"
            rows={7}
            placeholder="What are they asking for? Timeline, budget hints, scope, constraints…"
            className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500 text-sm" />

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">Tone</label>
              <select value={form.tone} onChange={(e) => upd("tone", e.target.value)}
                data-testid="proposal-tone-select"
                className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500">
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
                <option value="formal">Formal</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">Length</label>
              <select value={form.length} onChange={(e) => upd("length", e.target.value)}
                data-testid="proposal-length-select"
                className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500">
                <option value="short">Short (~400w)</option>
                <option value="standard">Standard (~800w)</option>
                <option value="detailed">Detailed (~1400w)</option>
              </select>
            </div>
          </div>

          <details className="mt-4 text-sm">
            <summary className="text-slate-500 cursor-pointer">Optional — email me a copy</summary>
            <div className="mt-2 space-y-2">
              <input value={form.email} onChange={(e) => upd("email", e.target.value)}
                data-testid="proposal-email-input"
                placeholder="you@company.com"
                className="w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500" />
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.consent} onChange={(e) => upd("consent", e.target.checked)} data-testid="proposal-consent" />
                I agree to be contacted.
              </label>
            </div>
          </details>

          <button onClick={submit} disabled={loading || form.brief.trim().length < 20}
            data-testid="proposal-submit"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-3 rounded font-bold transition disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</> : <><Sparkles className="w-4 h-4" /> Draft proposal</>}
          </button>
          {err && <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3" data-testid="proposal-error">{err}</div>}
        </div>

        <div className="lg:col-span-3">
          {!result && !loading && !streamText && (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 p-12 text-center text-slate-500 bg-white/50">
              <FileSignature className="w-10 h-10 mx-auto text-slate-300" />
              <div className="mt-3 text-sm">Your draft proposal will appear here.</div>
            </div>
          )}
          {loading && streamText && (
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="proposal-streaming">
              <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-indigo-50">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-indigo-700">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Streaming — {streamText.length} chars
                </div>
                <div className="text-[10px] text-slate-500">gpt-5.4 · live tokens</div>
              </div>
              <div className="p-6 prose prose-slate prose-sm max-w-none proposal-md">
                <MarkdownLite text={streamText} />
                <span className="inline-block w-2 h-4 bg-indigo-600 animate-pulse ml-0.5 align-middle" />
              </div>
            </div>
          )}
          {loading && !streamText && (
            <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-indigo-600" />
              <div className="mt-3 text-sm text-slate-600">Connecting to the proposal engine…</div>
            </div>
          )}
          {result && (
            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="proposal-output">
              <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Proposal ready</div>
                <div className="flex items-center gap-3">
                  {result.quote_total ? (
                    <div className="text-sm text-indigo-700 font-bold" data-testid="proposal-quote-total">
                      Quote hint: ${result.quote_total.toLocaleString()} {result.currency}
                    </div>
                  ) : null}
                  <button onClick={copy} data-testid="proposal-copy"
                    className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 hover:text-indigo-700 border border-slate-300 hover:border-indigo-400 rounded px-3 py-1.5 bg-white transition">
                    {copied ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy markdown</>}
                  </button>
                </div>
              </div>
              <div className="p-6 prose prose-slate prose-sm max-w-none proposal-md">
                <MarkdownLite text={result.proposal_markdown} />
              </div>
            </div>
          )}
        </div>
      </div>
    </AssistantShell>
  );
}

// A tiny markdown renderer that handles h1/h2/h3/bullet/bold/paragraph — no deps.
function MarkdownLite({ text }) {
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const out = [];
  let ulBuf = [];
  const flushUl = (key) => {
    if (ulBuf.length) {
      out.push(<ul key={key} className="list-disc pl-6 my-3 space-y-1">{ulBuf.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(li) }} />)}</ul>);
      ulBuf = [];
    }
  };
  const inline = (s) =>
    s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/`([^`]+)`/g, "<code class='bg-slate-100 px-1 rounded text-[13px]'>$1</code>")
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-indigo-600 underline" target="_blank" rel="noreferrer">$1</a>');
  lines.forEach((raw, i) => {
    const l = raw.trimEnd();
    if (l.startsWith("### ")) { flushUl(`u${i}`); out.push(<h3 key={i} className="mt-5 mb-2 text-base font-bold text-slate-900">{l.slice(4)}</h3>); }
    else if (l.startsWith("## ")) { flushUl(`u${i}`); out.push(<h2 key={i} className="mt-6 mb-2 text-lg font-bold text-slate-900">{l.slice(3)}</h2>); }
    else if (l.startsWith("# ")) { flushUl(`u${i}`); out.push(<h1 key={i} className="mt-4 mb-3 text-2xl font-bold text-slate-900">{l.slice(2)}</h1>); }
    else if (/^[-*]\s+/.test(l)) { ulBuf.push(l.replace(/^[-*]\s+/, "")); }
    else if (l.trim() === "") { flushUl(`u${i}`); }
    else { flushUl(`u${i}`); out.push(<p key={i} className="my-2 text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(l) }} />); }
  });
  flushUl("u-final");
  return <>{out}</>;
}


// ---------------------------------------------------------------------------
// 3. Sales Coach
// ---------------------------------------------------------------------------
function SalesCoach() {
  const [form, setForm] = useState({
    transcript: `AGENT: Hey, this is Chris from LitLab. Saw you filled out our form.
PROSPECT: Yeah, we've got a batch of failed welds, need a report by Friday.
AGENT: Cool. So we do metallurgical testing.
PROSPECT: OK. How much?
AGENT: Depends. I'll email you.`,
    call_type: "discovery",
    agent_name: "Chris",
    email: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr(""); setResult(null); setLoading(true);
    try {
      const r = await assistantSalesCoach({ ...form, email: form.email || null });
      setResult(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Coaching failed. Please retry.");
    } finally { setLoading(false); }
  };

  const gradeTone = (g) => ({ A: "emerald", B: "emerald", C: "amber", D: "rose", F: "rose" }[g] || "slate");

  return (
    <AssistantShell
      Icon={Headphones}
      title="AI Sales Coach"
      subtitle="Paste a call transcript — get a scorecard, wins, misses and a specific next-call plan."
      testIdPrefix="coach"
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-slate-200 p-6 h-fit sticky top-24">
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Call transcript</label>
          <textarea value={form.transcript} onChange={(e) => upd("transcript", e.target.value)}
            data-testid="coach-transcript-input"
            rows={10}
            placeholder={"AGENT: …\nPROSPECT: …"}
            className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500 text-sm font-mono" />

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">Call type</label>
              <select value={form.call_type} onChange={(e) => upd("call_type", e.target.value)}
                data-testid="coach-type-select"
                className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500">
                <option value="discovery">Discovery</option>
                <option value="follow-up">Follow-up</option>
                <option value="closing">Closing</option>
                <option value="support">Support</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest font-bold text-slate-500">Agent name</label>
              <input value={form.agent_name} onChange={(e) => upd("agent_name", e.target.value)}
                data-testid="coach-agent-input"
                className="mt-1 w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500" />
            </div>
          </div>

          <details className="mt-4 text-sm">
            <summary className="text-slate-500 cursor-pointer">Optional — email me the report</summary>
            <div className="mt-2 space-y-2">
              <input value={form.email} onChange={(e) => upd("email", e.target.value)}
                data-testid="coach-email-input"
                placeholder="you@company.com"
                className="w-full px-3 py-2 rounded border border-slate-300 outline-none focus:border-indigo-500" />
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={form.consent} onChange={(e) => upd("consent", e.target.checked)} data-testid="coach-consent" />
                I agree to be contacted.
              </label>
            </div>
          </details>

          <button onClick={submit} disabled={loading || form.transcript.trim().length < 40}
            data-testid="coach-submit"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-3 rounded font-bold transition disabled:opacity-50">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Coaching…</> : <><Target className="w-4 h-4" /> Coach this call</>}
          </button>
          {err && <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3" data-testid="coach-error">{err}</div>}
        </div>

        <div className="lg:col-span-3 space-y-5">
          {!result && !loading && (
            <div className="rounded-2xl border-2 border-dashed border-slate-300 p-12 text-center text-slate-500 bg-white/50">
              <Headphones className="w-10 h-10 mx-auto text-slate-300" />
              <div className="mt-3 text-sm">Your coaching report will appear here.</div>
            </div>
          )}
          {loading && (
            <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-indigo-600" />
              <div className="mt-3 text-sm text-slate-600">Analyzing the call…</div>
            </div>
          )}
          {result && (
            <>
              <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="coach-scorecard">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Overall</div>
                    <div className="mt-1 flex items-baseline gap-3">
                      <div className="text-5xl font-bold text-slate-900">{result.coaching.overall_score ?? "-"}</div>
                      {result.coaching.grade && (
                        <div className={`text-2xl font-black`}
                          style={{ color: gradeTone(result.coaching.grade) === "emerald" ? "#059669" :
                                           gradeTone(result.coaching.grade) === "amber" ? "#d97706" : "#e11d48" }}>
                          Grade {result.coaching.grade}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-slate-600 max-w-2xl text-sm">{result.coaching.coaching_summary}</div>
                  </div>
                </div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-3">
                  {(result.coaching.scorecard || []).map((s, i) => {
                    const tone = s.score >= 70 ? "emerald" : s.score >= 40 ? "amber" : "rose";
                    return (
                      <div key={i} data-testid={`coach-dim-${i}`}
                        className="rounded-lg border p-3"
                        style={{
                          background: tone === "emerald" ? "#ecfdf5" : tone === "amber" ? "#fffbeb" : "#fef2f2",
                          borderColor: tone === "emerald" ? "#a7f3d0" : tone === "amber" ? "#fde68a" : "#fecaca",
                        }}>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-600">{s.dimension}</div>
                        <div className="mt-1 text-2xl font-bold text-slate-900">{s.score}</div>
                        <div className="mt-1 text-[11px] text-slate-600 leading-snug">{s.note}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="coach-wins">
                  <div className="flex items-center gap-2 text-sm font-bold text-emerald-700">
                    <CheckCircle2 className="w-4 h-4" /> What went well
                  </div>
                  <ul className="mt-3 space-y-2">
                    {(result.coaching.wins || []).map((w, i) => (
                      <li key={i} className="text-sm text-slate-700 flex gap-2">
                        <span className="text-emerald-600 mt-0.5">•</span>{w}
                      </li>
                    ))}
                    {(!result.coaching.wins || result.coaching.wins.length === 0) && <li className="text-sm text-slate-400">No clear wins detected.</li>}
                  </ul>
                </div>
                <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="coach-misses">
                  <div className="flex items-center gap-2 text-sm font-bold text-rose-700">
                    <XCircle className="w-4 h-4" /> What to fix
                  </div>
                  <ul className="mt-3 space-y-2">
                    {(result.coaching.misses || []).map((m, i) => (
                      <li key={i} className="text-sm text-slate-700 flex gap-2">
                        <span className="text-rose-600 mt-0.5">•</span>{m}
                      </li>
                    ))}
                    {(!result.coaching.misses || result.coaching.misses.length === 0) && <li className="text-sm text-slate-400">Nothing critical detected.</li>}
                  </ul>
                </div>
              </div>

              {result.coaching.risk_flags?.length > 0 && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="coach-risks">
                  <div className="flex items-center gap-2 text-sm font-bold text-amber-800">
                    <AlertTriangle className="w-4 h-4" /> Risk flags
                  </div>
                  <ul className="mt-2 space-y-1">
                    {result.coaching.risk_flags.map((r, i) => <li key={i} className="text-sm text-amber-900">• {r}</li>)}
                  </ul>
                </div>
              )}

              {result.coaching.next_call_plan && (
                <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-800 text-white p-6" data-testid="coach-plan">
                  <div className="flex items-center gap-2 text-sm font-bold">
                    <ClipboardList className="w-4 h-4" /> Plan for your next call
                  </div>
                  {result.coaching.next_call_plan.objective && (
                    <div className="mt-3">
                      <div className="text-[11px] uppercase tracking-widest text-indigo-200 font-bold">Objective</div>
                      <div className="text-sm mt-1">{result.coaching.next_call_plan.objective}</div>
                    </div>
                  )}
                  {result.coaching.next_call_plan.opening_line && (
                    <div className="mt-4">
                      <div className="text-[11px] uppercase tracking-widest text-indigo-200 font-bold">Opening line</div>
                      <div className="text-sm mt-1 italic">"{result.coaching.next_call_plan.opening_line}"</div>
                    </div>
                  )}
                  {result.coaching.next_call_plan.three_questions_to_ask?.length > 0 && (
                    <div className="mt-4">
                      <div className="text-[11px] uppercase tracking-widest text-indigo-200 font-bold">Three questions</div>
                      <ol className="mt-1 space-y-1 list-decimal pl-5 text-sm">
                        {result.coaching.next_call_plan.three_questions_to_ask.map((q, i) => <li key={i}>{q}</li>)}
                      </ol>
                    </div>
                  )}
                  {result.coaching.next_call_plan.prep_checklist?.length > 0 && (
                    <div className="mt-4">
                      <div className="text-[11px] uppercase tracking-widest text-indigo-200 font-bold">Prep checklist</div>
                      <ul className="mt-1 space-y-1 list-disc pl-5 text-sm">
                        {result.coaching.next_call_plan.prep_checklist.map((p, i) => <li key={i}>{p}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AssistantShell>
  );
}
